import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { DollarSign, Lock, Unlock } from 'lucide-react';
import API from '../api_config';

function formatCLP(amount) {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(amount);
}

export function POSSessionManager({ onSessionActive, selectedWarehouse, warehouses = [] }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Open Session State
  const [registers, setRegisters] = useState([]);
  const [selectedRegister, setSelectedRegister] = useState('');
  const [openingBalance, setOpeningBalance] = useState('');
  const [openNotes, setOpenNotes] = useState('');
  const [sessionWarehouse, setSessionWarehouse] = useState('');
  
  // Close Session State
  const [isClosing, setIsClosing] = useState(false);
  const [closingCash, setClosingCash] = useState('');
  const [closingNotes, setClosingNotes] = useState('');
  const [sessionSummary, setSessionSummary] = useState(null);

  useEffect(() => {
    fetchActiveSession();
  }, []);

  const fetchActiveSession = async () => {
    try {
      const response = await axios.get(`${API}/pos/sessions/active`);
      setSession(response.data); // data is session object or null
      if (response.data) {
        onSessionActive(response.data);
      } else {
        onSessionActive(null);
        fetchRegisters();
      }
    } catch (error) {
      console.error("Error fetching active session", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRegisters = async () => {
    try {
      // TODO: Filter by current user's warehouse if applicable?
      // For now list all registers available to the company
      const response = await axios.get(`${API}/pos/registers`);
      setRegisters(response.data);
    } catch (error) {
      console.error("Error fetching registers", error);
    }
  };

  const handleOpenSession = async () => {
    if (!selectedRegister) {
      toast.error('Seleccione una caja');
      return;
    }
    if (!openingBalance) {
      toast.error('Ingrese el monto de apertura');
      return;
    }

    try {
      const response = await axios.post(`${API}/pos/sessions/open`, {
        register_id: selectedRegister,
        warehouse_id: sessionWarehouse, // Send selected warehouse
        opening_balance: parseFloat(openingBalance),
        notes: openNotes
      });
      setSession(response.data);
      onSessionActive(response.data);
      toast.success('Caja abierta exitosamente');
    } catch (error) {
        toast.error('Error al abrir caja: ' + (error.response?.data?.detail || error.message));
    }
  };

  const prepareCloseSession = async () => {
    if (!session) return;
    try {
        const response = await axios.get(`${API}/pos/sessions/${session.id}/summary`);
        setSessionSummary(response.data);
        setIsClosing(true);
    } catch (error) {
        toast.error('Error al obtener resumen de sesión');
    }
  };

  const handleCloseSession = async () => {
     if (!closingCash) {
        toast.error('Ingrese el efectivo contado');
        return;
     }

     try {
        await axios.post(`${API}/pos/sessions/${session.id}/close`, {
            closing_cash: parseFloat(closingCash),
            notes: closingNotes
        });
        toast.success('Turno cerrado exitosamente');
        setSession(null);
        setSessionSummary(null);
        setIsClosing(false);
        setClosingCash('');
        onSessionActive(null);
        fetchRegisters(); // Refresh registers for next open
     } catch (error) {
        toast.error('Error al cerrar turno: ' + (error.response?.data?.detail || error.message));
     }
  };

  if (loading) return <div className="p-4 text-center">Cargando estado de caja...</div>;

  if (!session) {
    return (
      <Card className="w-full max-w-md mx-auto mt-8 border-orange-200 bg-orange-50 dark:bg-orange-950/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Unlock className="h-5 w-5 text-orange-600" />
            Abrir Caja (Iniciar Turno)
          </CardTitle>
          <CardDescription>
            Debe abrir una caja para comenzar a vender.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Seleccionar Caja</Label>
            <select 
                className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={selectedRegister}
                onChange={(e) => {
                    setSelectedRegister(e.target.value);
                    setSessionWarehouse(''); // Reset warehouse selection
                }}
            >
                <option value="">Seleccione una caja...</option>
                {registers
                    .filter(r => r.status === 'closed')
                    .map(reg => (
                    <option key={reg.id} value={reg.id}>{reg.name}</option>
                ))}
            </select>
          </div>

          {selectedRegister && (
            <div className="space-y-2">
                <Label>Seleccionar Bodega para Venta</Label>
                <select 
                    className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    value={sessionWarehouse}
                    onChange={(e) => setSessionWarehouse(e.target.value)}
                >
                    <option value="">Seleccione bodega...</option>
                    {(() => {
                        const reg = registers.find(r => r.id === selectedRegister);
                        if (!reg) return null;
                        
                        // If register has explicit warehouse_ids, use them
                        if (reg.warehouse_ids && reg.warehouse_ids.length > 0) {
                            return reg.warehouse_ids.map(wid => {
                                const wh = warehouses.find(w => w.id === wid);
                                return <option key={wid} value={wid}>{wh ? wh.name : 'Unknown'}</option>;
                            });
                        }
                        
                        // Fallback to single warehouse_id
                        const wh = warehouses.find(w => w.id === reg.warehouse_id);
                        return <option value={reg.warehouse_id}>{wh ? wh.name : 'Bodega Principal'}</option>;
                    })()}
                </select>
                <p className="text-xs text-muted-foreground">
                    Los productos disponibles dependerán de esta bodega.
                </p>
            </div>
          )}
          
          <div className="space-y-2">
            <Label>Monto de Apertura (Efectivo en Caja)</Label>
            <div className="relative">
                <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input 
                    type="number" 
                    placeholder="0" 
                    className="pl-8"
                    value={openingBalance}
                    onChange={(e) => setOpeningBalance(e.target.value)}
                />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notas (Opcional)</Label>
            <Input 
                placeholder="Ej. Turno mañana..." 
                value={openNotes}
                onChange={(e) => setOpenNotes(e.target.value)}
            />
          </div>

          <Button onClick={handleOpenSession} className="w-full bg-orange-600 hover:bg-orange-700 text-white">
            Abrir Caja
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
        <div className="flex items-center justify-between bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 p-3 rounded-lg mb-4">
            <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center">
                    <Unlock className="h-4 w-4 text-green-600 dark:text-green-300" />
                </div>
                <div>
                    <p className="text-sm font-semibold text-green-800 dark:text-green-300">Caja Abierta</p>
                    <p className="text-xs text-green-600 dark:text-green-400">
                        Inicio: {new Date(session.start_time).toLocaleTimeString()} | Base: {formatCLP(session.opening_balance)}
                    </p>
                </div>
            </div>
            <Button variant="outline" size="sm" onClick={prepareCloseSession} className="border-green-200 hover:bg-green-100 hover:text-green-700 dark:border-green-800 dark:hover:bg-green-900">
                Cerrar Turno
            </Button>
        </div>

        <Dialog open={isClosing} onOpenChange={setIsClosing}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Cerrar Turno (Arqueo de Caja)</DialogTitle>
                    <DialogDescription>
                        Ingrese el efectivo contado para finalizar el turno.
                    </DialogDescription>
                </DialogHeader>
                
                {sessionSummary && (
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="bg-muted p-2 rounded">
                                <span className="block text-muted-foreground text-xs">Fondo Inicial</span>
                                <span className="font-mono font-bold">{formatCLP(session.opening_balance)}</span>
                            </div>
                            <div className="bg-muted p-2 rounded">
                                <span className="block text-muted-foreground text-xs">Ventas Totales (Calc)</span>
                                <span className="font-mono font-bold">{formatCLP(sessionSummary.total_sales)}</span>
                            </div>
                        </div>

                        <div className="space-y-2">
                             <Label>Desglose de Ventas (Sistema)</Label>
                             <div className="text-sm space-y-1 bg-slate-50 dark:bg-slate-900 p-2 rounded border">
                                {Object.entries(sessionSummary.payment_methods_breakdown).map(([method, amount]) => (
                                    <div key={method} className="flex justify-between">
                                        <span className="capitalize">{method === 'credito' ? 'Tarjeta de Crédito' : (method === 'fiado' ? 'Crédito' : method)}</span>
                                        <span className="font-mono">{formatCLP(amount)}</span>
                                    </div>
                                ))}
                                {Object.keys(sessionSummary.payment_methods_breakdown).length === 0 && (
                                    <div className="text-muted-foreground text-center text-xs">Sin ventas registradas</div>
                                )}
                             </div>
                        </div>

                        <div className="space-y-2 pt-2 border-t">
                            <Label className="text-base">Efectivo Real (Contado)</Label>
                            <div className="relative">
                                <DollarSign className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                                <Input 
                                    type="number" 
                                    placeholder="0" 
                                    className="pl-8 text-lg font-bold"
                                    value={closingCash}
                                    onChange={(e) => setClosingCash(e.target.value)}
                                    autoFocus
                                />
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Ingrese el total de billetes y monedas que hay en la caja.
                            </p>
                        </div>
                        
                         <div className="space-y-2">
                            <Label>Notas de Cierre</Label>
                            <Input 
                                placeholder="Comentarios sobre diferencias..." 
                                value={closingNotes}
                                onChange={(e) => setClosingNotes(e.target.value)}
                            />
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => setIsClosing(false)}>Cancelar</Button>
                    <Button variant="destructive" onClick={handleCloseSession}>Confirmar Cierre</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </>
  );
}
